/**
 * Implementation primitives for the Map **alert-glyph** layer (REQ-UI-069;
 * ADR-029; ADR-032 §4.2) — source + glyph layer + selection-highlight
 * layer, paint composition, GeoJSON conversion. Split from
 * :file:`alert-layer.tsx` so the ``.tsx`` file exports only its React
 * component (``react-refresh/only-export-components``), mirroring
 * :file:`detection-layer-impl.ts`.
 *
 * # Glyphs
 *
 * SAPIENT Alerts render as small glyphs whose **shape** is the active Profile's
 * ``map.alert.glyph`` (GLYPHS batch; REQ-UI-088; the closed
 * :data:`map/glyphs.GLYPH_IDS` vocabulary, default ``diamond``) — a **MapLibre
 * SDF icon** (:func:`map/glyphs.registerGlyphIcons` bakes one white SDF per
 * shape) on a ``symbol`` layer — and whose **colour** is driven by the
 * Profile's ``map.alert.color_mode`` (inc6b; REQ-UI-088; ADR-033 §9.7) via
 * :func:`map/marker-styling.alertColorExpression`, applied as the symbol layer's
 * ``icon-color`` so the shape is configurable while the inc6b colour modes are
 * unchanged:
 *
 *   - ``single`` → a flat ``map.alert.single_color`` (fallback the shared
 *     design-system **alert accent** ``--alert-glyph`` magenta). The magenta
 *     default reads as "attention" but is deliberately a distinct hue from the
 *     detection glyph (cyan) and **not** the ``--status-unhealthy`` red, so a
 *     single-mode alert glyph never reads as an offline platform (REQ-UI-046).
 *   - ``by_alert_type`` (the Profile default) → a data-driven ``match`` on the
 *     feature's ``alert_type`` (SAPIENT ``AlertType``) against the
 *     ``--alert-type-*`` severity palette (ADR-033 §9.7).
 *   - ``by_priority`` → a data-driven ``match`` on ``priority`` (SAPIENT
 *     ``AlertPriority``) against the ``--alert-priority-*`` palette.
 *
 * In the severity modes the red/dark-red high-severity hues DO read as the same
 * danger red as an offline platform — that is the *intended* operator-chosen
 * severity semantic (the glyph shape + the selection ring keep an alert
 * distinct from a platform marker). The colour is a flat fill / per-feature
 * paint, not a size/opacity ramp, so the glyph stays legible per CLAUDE.md §8.1.
 *
 * # REQ-UI-069 valid-location gate — the alert pins ONLY when it resolves
 *
 * Alert position is **nullable** (REQ-INGEST-044): an alert whose source
 * region doesn't resolve to a point yields ``null`` lat/lng — a *normal*
 * state. Per REQ-UI-069 the "distinct ALERTS glyph [is] rendered only when
 * an alert resolves to a valid location (else list-only)", so the
 * FeatureCollection here is **filtered to alerts with a valid location**;
 * a null-position alert paints no map glyph and is surfaced only in the
 * Alerts panel + the notification surface. (:func:`useActiveAlerts` already
 * excludes dismissed alerts, so this layer never renders a dismissed pin.)
 *
 * Like the events / detections sources, the alerts source is **not
 * clustered** — alerts are sparse and each glyph must stay individually
 * clickable for the Map <-> Alerts selection edge (REQ-UI-051 / ADR-024 §5).
 *
 * # Selection highlight
 *
 * A dedicated stroke-only ring layer, filtered to the selected ``alert_id``
 * via :func:`setSelectedAlertHighlight` (a ``setFilter`` call — no source
 * rebuild). The ring is the shared **action accent**
 * (:func:`selection-ring.getSelectionRingColor` — the ``--brand`` token,
 * amber in dark / blue in light), matching the platform / event / detection
 * rings + the list panels' ``bg-brand`` stripe (REQ-UI-040 / REQ-UI-051), so
 * a selection reads identically across entity kinds.
 *
 * Cross-references:
 *   - REQ-UI-069 (Alert map glyph, valid-location gate) / REQ-UI-046
 *     (glyph-colour legibility) / REQ-UI-051 (Map <-> Alerts selection) /
 *     REQ-INGEST-044 (nullable position).
 *   - ADR-016 §4 (WebGL); ADR-024 §5 (selection contract); ADR-029 §5/§6
 *     (Alert priority + panel); ADR-032 §4.2 (Alert data model).
 *   - map/detection-layer-impl.ts (the canonical point-glyph layer pattern
 *     mirrored here).
 */
import type { Map as MapLibreMap, GeoJSONSource, FilterSpecification } from "maplibre-gl";
import { alertColorExpression, type AlertColorConfig } from "@/map/marker-styling";
import {
  SELECTION_RING_STROKE_OPACITY,
  SELECTION_RING_STROKE_WIDTH,
  getSelectionRingColor,
  selectionRingRadiusExpression,
} from "@/map/selection-ring";
import {
  glyphIconImage,
  iconSizeExpression,
  normalizeGlyphId,
  registerGlyphIcons,
} from "@/map/glyphs";
import { isMapUsable } from "@/map/map-ready";
import type { AlertRecord } from "@/alerts/alerts-store";

/** MapLibre GeoJSON source ID for the alerts layer. */
export const ALERTS_SOURCE_ID = "meridian-alerts-source";

/** MapLibre circle-layer ID for the alert glyphs. */
export const ALERTS_LAYER_ID = "meridian-alerts-layer";

/** MapLibre circle-layer ID for the selected-alert highlight ring. */
export const ALERTS_HIGHLIGHT_LAYER_ID = "meridian-alerts-highlight-layer";

/**
 * Sentinel alert_id the highlight filter uses when nothing is selected — a
 * value no real SAPIENT alert_id can equal, so the ring paints nothing.
 */
const HIGHLIGHT_NONE_SENTINEL = " __no_selected_alert__";

/**
 * Zoom -> glyph radius stops. Matches the event / detection stops so the
 * three SAPIENT overlays read at the same "ping" scale (distinct by hue,
 * not size).
 */
export const ALERT_RADIUS_ZOOM_STOPS: ReadonlyArray<readonly [number, number]> = [
  [0, 3],
  [7, 5],
  [14, 7],
];

/**
 * Convert the active-alerts buffer into a GeoJSON point FeatureCollection.
 *
 * **Filtered to alerts with a valid location** (REQ-UI-069): an alert whose
 * ``latitude_deg`` / ``longitude_deg`` is ``null`` does not resolve to a
 * point and renders no map glyph (it remains list-only in the Alerts panel).
 * The type guard also narrows the surviving coords to ``number`` for the
 * ``[longitude_deg, latitude_deg]`` tuple. ``useActiveAlerts`` already
 * excludes dismissed alerts upstream, so this never emits a dismissed pin.
 */
export function alertsToFeatureCollection(
  alerts: readonly AlertRecord[],
): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: "FeatureCollection",
    features: alerts
      .filter(
        (
          alert,
        ): alert is AlertRecord & {
          latitude_deg: number;
          longitude_deg: number;
        } => alert.latitude_deg !== null && alert.longitude_deg !== null,
      )
      .map((alert) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [alert.longitude_deg, alert.latitude_deg],
        },
        // ``alert_id`` is the selection-bus id + the highlight filter key;
        // ``priority`` + ``alert_type`` ride along so the data-driven colour
        // paint (REQ-UI-088 ``by_priority`` / ``by_alert_type``) can switch on
        // them without a source rebuild (both are SAPIENT enum ints).
        properties: {
          alert_id: alert.alert_id,
          priority: alert.priority,
          alert_type: alert.alert_type,
        },
      })),
  };
}

function highlightFilter(alertId: string | null): FilterSpecification {
  return ["==", ["get", "alert_id"], alertId ?? HIGHLIGHT_NONE_SENTINEL];
}

/**
 * Register the source + glyph layer + highlight layer on the live map.
 * Idempotent — on first call adds them; on subsequent calls updates the
 * source data via :meth:`GeoJSONSource.setData` (no teardown), so alert
 * arrivals / dismissals / expirations don't churn the layer.
 *
 * ``colorConfig`` is the active Profile's ``map.alert`` colour mode +
 * single colour (inc6b; REQ-UI-088; ADR-033 §9.7); ``glyph`` is its
 * ``map.alert.glyph`` shape (GLYPHS batch; default ``diamond``). On first attach
 * it registers the glyph SDF icons + composes the symbol layer's ``icon-image``
 * (the chosen glyph) + ``icon-color`` via
 * :func:`marker-styling.alertColorExpression`; on the existing-source path (a
 * data update, a theme flip, or a Profile Apply) it re-applies the icon image +
 * colour so a shape/mode/colour change — or the active-theme severity palette —
 * repaints the live layer without a teardown. Omit the args for the legacy
 * ``by_alert_type`` + ``diamond`` behaviour; an unknown glyph id falls back to
 * ``circle`` (:func:`normalizeGlyphId`).
 */
export function attachAlertLayer(
  map: MapLibreMap,
  alerts: readonly AlertRecord[],
  colorConfig?: AlertColorConfig,
  glyph?: string,
): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`; CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  const featureCollection = alertsToFeatureCollection(alerts);

  // Compose the colour value (flat hex in ``single`` mode, or a data-driven
  // ``match`` on alert_type / priority). The resolved-hex reads inside mean a
  // theme flip re-paints with the right palette on re-apply (REQ-UI-040).
  const colorValue = alertColorExpression(colorConfig);
  // Resolve the glyph shape (unknown id → circle) and its registered SDF id.
  const iconImage = glyphIconImage(normalizeGlyphId(glyph));

  // Ensure the glyph SDF icons exist before the symbol layer references them
  // (idempotent; safe regardless of map-load / attach ordering).
  registerGlyphIcons(map);

  const existing = map.getSource(ALERTS_SOURCE_ID) as GeoJSONSource | undefined;
  if (existing) {
    existing.setData(featureCollection);
    // Re-apply the glyph image + colour so a Profile shape/mode/colour change or
    // theme flip repaints the live glyph in place (REQ-UI-088 / REQ-UI-040).
    // No-op before the layer is registered (defensive).
    if (map.getLayer(ALERTS_LAYER_ID) !== undefined) {
      map.setLayoutProperty(ALERTS_LAYER_ID, "icon-image", iconImage);
      map.setPaintProperty(ALERTS_LAYER_ID, "icon-color", colorValue);
    }
    return;
  }

  map.addSource(ALERTS_SOURCE_ID, { type: "geojson", data: featureCollection });

  map.addLayer({
    id: ALERTS_LAYER_ID,
    type: "symbol",
    source: ALERTS_SOURCE_ID,
    layout: {
      "icon-image": iconImage,
      "icon-size": iconSizeExpression(ALERT_RADIUS_ZOOM_STOPS),
      // Sparse glyphs must always render (never collide away) — each must stay
      // individually clickable for the Map <-> Alerts selection edge.
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
    paint: {
      // SDF tint — the inc6b colour expression (flat hex or data-driven match;
      // REQ-UI-088). ``icon-opacity`` matches the prior circle ``circle-opacity``.
      "icon-color": colorValue,
      "icon-opacity": 0.9,
    },
  });

  // Selection ring on top — stroke-only, filtered to the selected alert.
  map.addLayer({
    id: ALERTS_HIGHLIGHT_LAYER_ID,
    type: "circle",
    source: ALERTS_SOURCE_ID,
    filter: highlightFilter(null),
    paint: {
      "circle-radius": selectionRingRadiusExpression(ALERT_RADIUS_ZOOM_STOPS),
      "circle-color": "#000000",
      "circle-opacity": 0,
      "circle-stroke-width": SELECTION_RING_STROKE_WIDTH,
      "circle-stroke-color": getSelectionRingColor(),
      "circle-stroke-opacity": SELECTION_RING_STROKE_OPACITY,
    },
  });
}

/**
 * Point the highlight ring at ``alertId`` (or clear it when ``null``) via a
 * ``setFilter`` call. No-op when the layer isn't registered yet.
 */
export function setSelectedAlertHighlight(map: MapLibreMap, alertId: string | null): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`.
  if (!isMapUsable(map)) {
    return;
  }
  if (!map.getLayer(ALERTS_HIGHLIGHT_LAYER_ID)) {
    return;
  }
  map.setFilter(ALERTS_HIGHLIGHT_LAYER_ID, highlightFilter(alertId));
}

/**
 * Tear down the highlight layer, glyph layer, and source on unmount (layers
 * before source, per MapLibre's ordering).
 */
export function detachAlertLayer(map: MapLibreMap): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`; CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  if (map.getLayer(ALERTS_HIGHLIGHT_LAYER_ID)) {
    map.removeLayer(ALERTS_HIGHLIGHT_LAYER_ID);
  }
  if (map.getLayer(ALERTS_LAYER_ID)) {
    map.removeLayer(ALERTS_LAYER_ID);
  }
  if (map.getSource(ALERTS_SOURCE_ID)) {
    map.removeSource(ALERTS_SOURCE_ID);
  }
}
